import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mrs. Bum logo
const LOGO = 'https://daily-obx-cdn.b-cdn.net/newsletter-images/mrsbum-logo.png';

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('mrsbum') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

function MrsBum() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { itemCount, openCart } = useCart();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/mrsbum/categories`);
      setCategories(response.data);
    } catch (error) {
      // Fallback to static categories
      setCategories([
        { id: 'bracelets', label: 'Bracelets', count: 0, image_url: null },
        { id: 'anklets', label: 'Anklets', count: 0, image_url: null },
        { id: 'tie-dye', label: 'Tie Dye Apparel', count: 0, image_url: null },
        { id: 'baskets', label: 'Woven Baskets', count: 0, image_url: null }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      {/* Hero Section */}
      <section 
        style={{
          background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 50%, #ddd6fe 100%)',
          minHeight: '350px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '50px 20px',
          textAlign: 'center'
        }}
      >
        <img 
          src={LOGO}
          alt="Mrs. Bum - Hand-Dyed Coastal Apparel - Est. 2020"
          style={{
            maxWidth: '320px',
            width: '85%',
            height: 'auto',
            marginBottom: '20px'
          }}
        />
        <p style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.15rem)',
          color: '#6b21a8',
          maxWidth: '500px',
          lineHeight: '1.6',
          margin: 0
        }}>
          Handcrafted coastal jewelry, tie-dye apparel & woven baskets &mdash; made with love on the Outer Banks.
        </p>
      </section>

      {/* Main Content - Category Cards */}
      <main style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: '60px 20px 80px' 
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="loading-spinner" />
          </div>
        ) : (
          <>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '28px',
              textAlign: 'center',
              marginBottom: '50px',
              color: '#1a1a1a'
            }}>
              Shop By Category
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '30px',
              justifyItems: 'center'
            }}>
              {categories.map(category => (
                <CategoryCard key={category.id} category={category} />
              ))}
            </div>

            <p style={{
              textAlign: 'center',
              marginTop: '50px',
              color: '#7c3aed',
              fontSize: '14px'
            }}>
              Free shipping on orders over $75 • US shipping only
            </p>
          </>
        )}
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

// Category Card Component - Tall vertical cards like the screenshot
function CategoryCard({ category }) {
  const hasProducts = category.count > 0;
  
  const cardContent = (
    <div 
      style={{
        width: '100%',
        maxWidth: '220px',
        cursor: hasProducts ? 'pointer' : 'default',
        transition: 'transform 0.2s',
        opacity: hasProducts ? 1 : 0.6
      }}
      className={hasProducts ? 'mrsbum-category-hover' : ''}
      data-testid={`category-${category.id}`}
    >
      {/* Tall Image Container */}
      <div style={{
        background: '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
        marginBottom: '15px'
      }}>
        <div style={{
          aspectRatio: '3/4',
          overflow: 'hidden',
          background: '#f8f4ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {category.image_url ? (
            <img 
              src={category.image_url} 
              alt={category.label}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#c4b5fd',
              padding: '20px',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '50px', marginBottom: '10px' }}>
                {category.id === 'bracelets' ? '📿' : 
                 category.id === 'anklets' ? '✨' :
                 category.id === 'tie-dye' ? '🌀' : '🧺'}
              </span>
              <span style={{ fontSize: '13px' }}>Coming Soon</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Label */}
      <h3 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '20px',
        fontWeight: '400',
        textAlign: 'center',
        margin: 0,
        color: '#1a1a1a'
      }}>
        {category.label}
      </h3>
      
      {category.count > 0 && (
        <p style={{
          textAlign: 'center',
          color: '#7c3aed',
          fontSize: '13px',
          margin: '5px 0 0'
        }}>
          {category.count} {category.count === 1 ? 'design' : 'designs'}
        </p>
      )}
    </div>
  );

  if (hasProducts) {
    return (
      <Link 
        to={`/shop/mrsbum/${category.id}`} 
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

export default MrsBum;
